import React, { useMemo } from 'react';
import { useTrading, useEngine } from '../context/MarketContext';
import { useAppSelector } from '../store/hooks';
import type { Position } from '../types';
import { colors } from '../styles/tokens';
import { toEAT } from '../utils/time';
import { INSTRUMENTS } from '../constants/instruments';
import { Panel } from '../components/ui';

function fmtTZS(n: number): string {
  if (n >= 1_000_000_000) return `TZS ${(n / 1_000_000_000).toFixed(1)}B`;
  if (n >= 1_000_000) return `TZS ${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `TZS ${(n / 1_000).toFixed(0)}K`;
  return `TZS ${n.toFixed(0)}`;
}

const SECID_BY_LABEL = INSTRUMENTS.reduce<Record<string, string>>((m, { label, secId }) => {
  m[label.toUpperCase()] = secId.toUpperCase();
  return m;
}, {});

function getSecId(symbol: string): string {
  const upperSymbol = symbol.toUpperCase();
  return SECID_BY_LABEL[upperSymbol] || upperSymbol;
}

export default function PositionsPage() {
  const { positions, orders, portfolio } = useTrading();
  const { inventoryBySymbol, performanceSnapshot, settlementReport } = useEngine();
  const vwapMap = useAppSelector(s => s.chartData.vwap);

  const { calculatedPositions, positionTotals } = useMemo(() => {
    const calculated: Array<Position & { vwapPrice?: number }> = [];
    let sumU = 0, sumR = 0, sumExposure = 0;

    positions.forEach(pos => {
      const secId = getSecId(pos.symbol);
      const vwapData = vwapMap[secId.toUpperCase()];
      const vwapPrice = vwapData && vwapData.length > 0 ? vwapData[vwapData.length - 1].vwap : undefined;

      // Always recalculate unrealizedPnL client-side using latest VWAP for real-time updates.
      // Backend only recalculates on fills, so its value is stale between fills.
      const unrealizedPnL = (vwapPrice != null && pos.avgPrice && pos.quantity)
        ? (vwapPrice - pos.avgPrice) * pos.quantity
        : pos.unrealizedPnL;

      const realizedPnL = pos.realizedPnL;
      const totalPnL = unrealizedPnL + realizedPnL;
      // Use vwapPrice for display only (current column), not for PnL math above
      const displayPrice = vwapPrice ?? pos.currentPrice;
      const exposure = displayPrice ? displayPrice * Math.abs(pos.quantity) : (pos.exposure ?? 0);

      calculated.push({ ...pos, currentPrice: displayPrice, unrealizedPnL, totalPnL, exposure });
      sumU += unrealizedPnL;
      sumR += realizedPnL;
      sumExposure += exposure;
    });

    return {
      calculatedPositions: calculated,
      positionTotals: { totalUnrealizedPnL: sumU, totalRealizedPnL: sumR, totalPnL: sumU + sumR, totalExposure: sumExposure }
    };
  }, [positions, vwapMap]);

  // Prefer backend PORTFOLIO_TOTALS (authoritative, same VWAP used for position PnL).
  // Fall back to summing calculatedPositions only if backend hasn't sent totals yet.
  const hasBackendTotals = portfolio.totalPnL !== 0 || portfolio.totalRealizedPnL !== 0 || portfolio.totalUnrealizedPnL !== 0;
  const totalU = hasBackendTotals ? portfolio.totalUnrealizedPnL : positionTotals.totalUnrealizedPnL;
  const totalR = hasBackendTotals ? portfolio.totalRealizedPnL : positionTotals.totalRealizedPnL;
  const totalPnL = hasBackendTotals ? portfolio.totalPnL : positionTotals.totalPnL;
  const totalExposure = hasBackendTotals ? portfolio.totalExposure : positionTotals.totalExposure;

  // Per-symbol cash allocation: use backend SETTLEMENT_UPDATE whenever available.
  // Only derive from open orders when settlement data is entirely unavailable.
  const { committedBySym, receivablesBySym, reservedBySym, capitalBySym, tradeableBySym, cashSymbols, orderDerivedCommitted, orderDerivedReceivables } = useMemo(() => {
    const committed = new Map<string, number>();
    const receivables = new Map<string, number>();
    const reserved = new Map<string, number>();
    const capital = new Map<string, number>();
    const tradeable = new Map<string, number>();
    if (settlementReport) {
      for (const [sym, val] of Object.entries(settlementReport.committed_by_ticker ?? {})) {
        const key = sym.toUpperCase();
        if (Number(val) > 0) committed.set(key, Number(val));
      }
      for (const [sym, val] of Object.entries(settlementReport.receivables_by_ticker ?? {})) {
        const key = sym.toUpperCase();
        if (Number(val) > 0) receivables.set(key, Number(val));
      }
      for (const [sym, val] of Object.entries(settlementReport.reserved_by_ticker ?? {})) {
        const key = sym.toUpperCase();
        if (Number(val) > 0) reserved.set(key, Number(val));
      }
      for (const [sym, val] of Object.entries(settlementReport.capital_by_ticker ?? {})) {
        const key = sym.toUpperCase();
        if (Number(val) > 0) capital.set(key, Number(val));
      }
      for (const [sym, val] of Object.entries(settlementReport.tradeable_by_ticker ?? {})) {
        const key = sym.toUpperCase();
        tradeable.set(key, Number(val));
      }
    } else {
      for (const o of orders) {
        if (!['new', 'partially_filled'].includes(o.status)) continue;
        const remaining = o.leavesQty * o.price;
        const key = o.symbol.toUpperCase();
        if (o.side === 'buy') committed.set(key, (committed.get(key) ?? 0) + remaining);
        else receivables.set(key, (receivables.get(key) ?? 0) + remaining);
      }
    }
    const syms = Array.from(new Set([
      ...Array.from(committed.keys()),
      ...Array.from(receivables.keys()),
      ...Array.from(reserved.keys()),
      ...Array.from(capital.keys()),
    ])).sort();
    const orderDerivedCommitted = Array.from(committed.values()).reduce((a, b) => a + b, 0);
    const orderDerivedReceivables = Array.from(receivables.values()).reduce((a, b) => a + b, 0);
    return { committedBySym: committed, receivablesBySym: receivables, reservedBySym: reserved, capitalBySym: capital, tradeableBySym: tradeable, cashSymbols: syms, orderDerivedCommitted, orderDerivedReceivables };
  }, [orders, settlementReport?.committed_by_ticker, settlementReport?.receivables_by_ticker, settlementReport?.reserved_by_ticker, settlementReport?.capital_by_ticker, settlementReport?.tradeable_by_ticker]);

  // Totals: backend SETTLEMENT_UPDATE is authoritative when present.
  const isInstantMode = settlementReport?.mode === 'instant';
  const displayReserved = settlementReport ? (settlementReport.reserved_cash ?? 0) : 0;
  const displayCommitted = settlementReport ? settlementReport.committed_cash : orderDerivedCommitted;
  const displayReceivables = settlementReport ? settlementReport.receivables : (isInstantMode ? 0 : orderDerivedReceivables);
  const committedFromOrders = !settlementReport && orderDerivedCommitted > 0;
  const receivablesFromOrders = !settlementReport && !isInstantMode && orderDerivedReceivables > 0;

  // Cash utilization bar: available_cash = tradeable + reserved + committed
  const cashPool = settlementReport ? settlementReport.available_cash : 0;
  const reservedPct  = cashPool > 0 ? (displayReserved  / cashPool) * 100 : 0;
  const committedPct = cashPool > 0 ? (displayCommitted / cashPool) * 100 : 0;
  const availablePct = Math.max(0, 100 - reservedPct - committedPct);

  // Settlement date max net for bar scaling
  const maxNetAbs = settlementReport
    ? Math.max(...settlementReport.settlement_dates.map(d => Math.abs(d.net)), 1)
    : 1;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

      {/* ── Zone 1: Cash & Settlement Dashboard ── */}
      <Panel
        title="Cash & settlement"
        badge={settlementReport?.as_of ? `as of ${settlementReport.as_of.slice(11, 19)} EAT` : undefined}
      >

        {/* Cash balance cards — 5-column grid */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr', gap: 10, marginBottom: 12 }}>
          <CashCard
            label="Available Cash"
            value={settlementReport ? fmtTZS(settlementReport.available_cash) : '—'}
            color="#d7dde4"
            sub="total cash pool"
          />
          <CashCard
            label="Tradeable Cash"
            value={settlementReport ? fmtTZS(settlementReport.tradeable_cash) : '—'}
            color="#36c98e"
            sub={settlementReport ? `${availablePct.toFixed(0)}% of available` : undefined}
          />
          <CashCard
            label="Reserved"
            value={settlementReport ? fmtTZS(displayReserved) : '—'}
            color="#a78bfa"
            sub={settlementReport ? `${reservedPct.toFixed(0)}% · open orders` : undefined}
          />
          <CashCard
            label="Committed"
            value={settlementReport ? fmtTZS(displayCommitted) : '—'}
            color="#ff8c3a"
            sub={settlementReport ? `${committedPct.toFixed(0)}% · filled T+2${committedFromOrders ? ' (orders)' : ''}` : undefined}
          />
          <CashCard
            label="Receivables"
            value={settlementReport ? fmtTZS(displayReceivables) : '—'}
            color="#4da3ff"
            sub={receivablesFromOrders ? 'from open orders' : 'pending sells'}
          />
        </div>

        {/* Cash utilization bar: committed | reserved | tradeable */}
        {settlementReport && cashPool > 0 && (
          <div style={{ marginBottom: 12 }}>
            <div style={{ height: 10, borderRadius: 5, overflow: 'hidden', display: 'flex', backgroundColor: colors.bg.primary }}>
              <div style={{ width: `${committedPct}%`, backgroundColor: colors.accent.yellow, transition: 'width 0.4s' }} />
              <div style={{ width: `${reservedPct}%`, backgroundColor: '#a78bfa', transition: 'width 0.4s' }} />
              <div style={{ flex: 1, backgroundColor: colors.accent.green }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, color: colors.text.muted, marginTop: 3 }}>
              <span style={{ color: colors.accent.yellow }}>Committed {fmtTZS(displayCommitted)}{committedFromOrders ? ' (orders)' : ''}</span>
              <span style={{ color: '#a78bfa' }}>Reserved {fmtTZS(displayReserved)}</span>
              <span style={{ color: colors.accent.green }}>Tradeable {fmtTZS(settlementReport.tradeable_cash)}</span>
            </div>
          </div>
        )}

        {/* Settlement schedule table */}
        {settlementReport && settlementReport.settlement_dates.length > 0 && (
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: colors.text.muted, letterSpacing: '0.06em', marginBottom: 6 }}>SETTLEMENT SCHEDULE</div>
            {/* Header */}
            <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr 1fr 1fr 80px', gap: 8, padding: '4px 0', borderBottom: `1px solid ${colors.border.default}`, fontSize: 9, fontWeight: 700, color: colors.border.default, letterSpacing: '0.06em' }}>
              <div>DATE</div>
              <div style={{ textAlign: 'right' }}>INFLOWS</div>
              <div style={{ textAlign: 'right' }}>OUTFLOWS</div>
              <div style={{ textAlign: 'right' }}>NET</div>
              <div />
            </div>
            {settlementReport.settlement_dates.slice(0, 3).map((sd, i) => {
              const netPct = (Math.abs(sd.net) / maxNetAbs) * 100;
              const isPos = sd.net >= 0;
              return (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: '80px 1fr 1fr 1fr 80px', gap: 8, padding: '6px 0', borderBottom: '1px solid #0a0d10', alignItems: 'center' }}>
                  <div style={{ fontSize: 10, color: colors.text.secondary, fontWeight: 600 }}>
                    T+{i + 1} <span style={{ color: colors.text.muted, fontWeight: 400 }}>({sd.date.slice(5)})</span>
                  </div>
                  <div style={{ textAlign: 'right', fontSize: 10, color: colors.accent.green }}>
                    {sd.inflows > 0 ? `+${fmtTZS(sd.inflows)}` : '—'}
                  </div>
                  <div style={{ textAlign: 'right', fontSize: 10, color: colors.accent.red }}>
                    {sd.outflows > 0 ? `−${fmtTZS(sd.outflows)}` : '—'}
                  </div>
                  <div style={{ textAlign: 'right', fontSize: 11, fontWeight: 700, color: isPos ? colors.accent.green : colors.accent.red }}>
                    {isPos ? '+' : ''}{fmtTZS(sd.net)}
                  </div>
                  {/* Mini bar */}
                  <div style={{ height: 6, backgroundColor: colors.bg.primary, borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{ width: `${netPct}%`, height: '100%', backgroundColor: isPos ? colors.accent.green : colors.accent.red, borderRadius: 3 }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Panel>

      {/* ── Zone 1b: Cash Allocation by Counter ── */}
      <Panel title="Cash allocation by counter">
        {cashSymbols.length === 0 && inventoryBySymbol.size === 0 ? (
          <div style={{ fontSize: 12, color: colors.border.default, fontStyle: 'italic' }}>No cash allocated per counter</div>
        ) : (
          <>
            {/* Header */}
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(68px,0.85fr) repeat(6, minmax(78px,1fr)) minmax(80px,0.95fr)', gap: 8, padding: '4px 0', borderBottom: `1px solid ${colors.border.default}`, fontSize: 9, fontWeight: 700, color: colors.text.muted, letterSpacing: '0.06em' }}>
              <div>COUNTER</div>
              <div style={{ textAlign: 'right' }}>CAPITAL BUDGET</div>
              <div style={{ textAlign: 'right' }}>TRADEABLE</div>
              <div style={{ textAlign: 'right' }}>RESERVED</div>
              <div style={{ textAlign: 'right' }}>COMMITTED</div>
              <div style={{ textAlign: 'right' }}>RECEIVABLES</div>
              <div style={{ textAlign: 'right' }}>POSITION</div>
              <div style={{ textAlign: 'right' }}>UTILIZATION</div>
            </div>
            {/* Rows: union of all symbols with any data */}
            {Array.from(new Set([
              ...cashSymbols,
              ...Array.from(inventoryBySymbol.keys()),
            ])).sort().map(sym => {
              const reserved    = reservedBySym.get(sym) ?? 0;
              const committed   = committedBySym.get(sym) ?? 0;
              const receivable  = receivablesBySym.get(sym) ?? 0;
              const capAlloc    = capitalBySym.get(sym) ?? 0;
              const tradeableCtr = tradeableBySym.get(sym) ?? 0;
              const inv         = inventoryBySymbol.get(sym);
              const qty         = inv?.quantity ?? 0;
              const maxPos      = inv?.max_position;
              const utilPct     = (inv?.utilization ?? 0) * 100;
              const canBuy      = inv?.can_buy !== false;
              const label       = INSTRUMENTS.find(i => i.secId.toUpperCase() === sym || i.label.toUpperCase() === sym)?.label ?? sym;
              // Only render row if there's any data to show
              if (reserved === 0 && committed === 0 && receivable === 0 && qty === 0 && capAlloc === 0 && !inv) return null;
              return (
                <div key={sym} style={{ display: 'grid', gridTemplateColumns: 'minmax(68px,0.85fr) repeat(6, minmax(78px,1fr)) minmax(80px,0.95fr)', gap: 8, padding: '7px 0', borderBottom: '1px solid #0a0d10', alignItems: 'center' }}>
                  {/* Counter */}
                  <div style={{ fontSize: 12, fontWeight: 700, color: colors.text.primary }}>{label}</div>
                  {/* Capital Allocated */}
                  <div style={{ textAlign: 'right', fontSize: 11, color: capAlloc > 0 ? colors.text.secondary : colors.border.default, fontVariantNumeric: 'tabular-nums' }}>
                    {capAlloc > 0 ? fmtTZS(capAlloc) : '—'}
                  </div>
                  {/* Tradeable per counter */}
                  <div style={{ textAlign: 'right', fontSize: 11, color: tradeableCtr > 0 ? colors.accent.green : colors.border.default, fontVariantNumeric: 'tabular-nums' }}>
                    {capAlloc > 0 ? fmtTZS(tradeableCtr) : '—'}
                  </div>
                  {/* Reserved (open BID orders, pre-fill) */}
                  <div style={{ textAlign: 'right', fontSize: 11, color: reserved > 0 ? '#a78bfa' : colors.border.default, fontVariantNumeric: 'tabular-nums' }}>
                    {reserved > 0 ? fmtTZS(reserved) : '—'}
                  </div>
                  {/* Committed (filled, awaiting T+2 settlement) */}
                  <div style={{ textAlign: 'right', fontSize: 11, color: committed > 0 ? colors.accent.yellow : colors.border.default, fontVariantNumeric: 'tabular-nums' }}>
                    {committed > 0 ? fmtTZS(committed) : '—'}
                  </div>
                  {/* Receivables (sell proceeds, awaiting T+2) */}
                  <div style={{ textAlign: 'right', fontSize: 11, color: receivable > 0 ? colors.accent.blue : colors.border.default, fontVariantNumeric: 'tabular-nums' }}>
                    {receivable > 0 ? fmtTZS(receivable) : '—'}
                  </div>
                  {/* Position qty / max */}
                  <div style={{ textAlign: 'right', fontSize: 11, color: qty > 0 ? colors.text.primary : colors.border.default, fontVariantNumeric: 'tabular-nums' }}>
                    {qty > 0 ? (
                      <>
                        <span style={{ fontWeight: 700 }}>{qty.toLocaleString()}</span>
                        {maxPos ? <span style={{ color: colors.text.muted, fontSize: 9 }}> / {maxPos.toLocaleString()}</span> : null}
                      </>
                    ) : '—'}
                  </div>
                  {/* Utilization bar + pct */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5, justifyContent: 'flex-end' }}>
                    <div style={{ width: 48, height: 5, backgroundColor: colors.bg.primary, borderRadius: 3, overflow: 'hidden' }}>
                      <div style={{ width: `${Math.min(100, utilPct)}%`, height: '100%', backgroundColor: utilPct > 85 ? colors.accent.red : utilPct > 60 ? colors.accent.yellow : colors.accent.green, borderRadius: 3 }} />
                    </div>
                    <span style={{ fontSize: 10, color: utilPct > 85 ? colors.accent.red : utilPct > 60 ? colors.accent.yellow : colors.text.secondary, fontVariantNumeric: 'tabular-nums', minWidth: 28, textAlign: 'right' }}>
                      {utilPct > 0 ? `${utilPct.toFixed(0)}%` : (canBuy ? '—' : 'FULL')}
                    </span>
                  </div>
                </div>
              );
            })}
          </>
        )}
      </Panel>

      {/* ── Zone 2: Portfolio P&L KPIs ── */}
      <Panel title="Portfolio P&L">
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {[
            { label: 'Unrealized P&L', val: totalU },
            { label: 'Realized P&L', val: totalR },
            { label: 'Total P&L', val: totalPnL },
            { label: 'Exposure', val: totalExposure, neutral: true },
          ].map(({ label, val, neutral }) => (
            <div key={label} style={{ background: colors.bg.primary, borderRadius: 6, border: `1px solid ${colors.border.default}`, padding: '8px 12px', flex: 1, minWidth: 140 }}>
              <div style={{ fontSize: 9, color: colors.text.muted, fontWeight: 700, letterSpacing: '0.06em', marginBottom: 4 }}>{label.toUpperCase()}</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: neutral ? colors.text.secondary : val >= 0 ? colors.accent.green : colors.accent.red, fontVariantNumeric: 'tabular-nums' }}>
                {val.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
              </div>
            </div>
          ))}
          {performanceSnapshot && (
            <div style={{ background: colors.bg.primary, borderRadius: 6, border: `1px solid ${colors.border.default}`, padding: '8px 12px', flex: 1, minWidth: 140 }}>
              <div style={{ fontSize: 9, color: colors.text.muted, fontWeight: 700, letterSpacing: '0.06em', marginBottom: 4 }}>MAX DRAWDOWN</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: colors.accent.red }}>
                {(performanceSnapshot.max_drawdown * 100).toFixed(1)}%
              </div>
            </div>
          )}
        </div>
      </Panel>

      {/* Gross exposure drives the SHARE column and the TOTAL row. Marked at the live price
          where there is one, else at cost. */}
      {/* ── Zone 4: P&L Attribution (was two tables; the standalone attribution table below
             duplicated these same symbols and P&L figures, so it was folded in here) ── */}
      <Panel title="P&L attribution" badge={`${calculatedPositions.length} symbols`} flush>
        <div style={{ fontSize: 10, color: colors.text.muted, padding: '10px 16px 0' }}>by symbol · current session</div>
        <HeaderRow />
        <div style={{ maxHeight: 420, overflow: 'auto' }}>
          {calculatedPositions.length === 0 ? (
            <div style={{ padding: '20px', textAlign: 'center', color: colors.text.secondary, fontSize: 13 }}>No positions yet.</div>
          ) : (
            calculatedPositions.map((p, i) => (<Row key={i} p={p} grossExposure={totalExposure} />))
          )}
        </div>
        {calculatedPositions.length > 0 && <TotalRow rows={calculatedPositions} grossExposure={totalExposure} />}
      </Panel>

      {/* ── Zone 3: Inventory by Symbol (enhanced) ── */}
      {inventoryBySymbol.size > 0 && (
        <Panel title="Inventory status" badge={`${inventoryBySymbol.size} symbols`}>
          {/* Table header */}
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(56px,0.7fr) minmax(74px,1fr) minmax(74px,1fr) minmax(80px,1.05fr) minmax(80px,1.05fr) minmax(88px,1.15fr) minmax(72px,0.9fr)', gap: 6, padding: '4px 0', borderBottom: `1px solid ${colors.border.default}`, fontSize: 9, fontWeight: 700, color: colors.border.default, letterSpacing: '0.06em' }}>
            <div>SYM</div>
            <div style={{ textAlign: 'right' }}>POSITION</div>
            <div style={{ textAlign: 'right' }}>SETTLED</div>
            <div style={{ textAlign: 'right' }}>UNSETTL BUY</div>
            <div style={{ textAlign: 'right' }}>UNSETTL SELL</div>
            <div style={{ textAlign: 'center' }}>SKEW</div>
            <div style={{ textAlign: 'center' }}>STATUS</div>
          </div>
          {Array.from(inventoryBySymbol.entries()).map(([secId, inv]) => {
            const label = INSTRUMENTS.find(i => i.secId.toUpperCase() === secId.toUpperCase())?.label ?? secId;
            const skewPct = Math.abs(inv.skew) * 100;
            const skewColor = inv.skew > 0.1 ? colors.accent.green : inv.skew < -0.1 ? colors.accent.red : colors.text.secondary;
            return (
              <div key={secId} style={{ display: 'grid', gridTemplateColumns: 'minmax(56px,0.7fr) minmax(74px,1fr) minmax(74px,1fr) minmax(80px,1.05fr) minmax(80px,1.05fr) minmax(88px,1.15fr) minmax(72px,0.9fr)', gap: 6, padding: '7px 0', borderBottom: '1px solid #0a0d10', alignItems: 'center' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: colors.text.primary }}>{label}</div>
                <div style={{ textAlign: 'right', fontSize: 11, color: inv.quantity >= 0 ? colors.accent.green : colors.accent.red, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                  {inv.quantity >= 0 ? '+' : ''}{inv.quantity.toLocaleString()}
                </div>
                <div style={{ textAlign: 'right', fontSize: 11, color: colors.text.secondary, fontVariantNumeric: 'tabular-nums' }}>
                  {inv.settled_quantity.toLocaleString()}
                </div>
                <div style={{ textAlign: 'right', fontSize: 11, color: inv.unsettled_buy_qty > 0 ? colors.accent.green : colors.text.muted, fontVariantNumeric: 'tabular-nums' }}>
                  {inv.unsettled_buy_qty > 0 ? `+${inv.unsettled_buy_qty.toLocaleString()}` : '—'}
                </div>
                <div style={{ textAlign: 'right', fontSize: 11, color: inv.unsettled_sell_qty > 0 ? colors.accent.red : colors.text.muted, fontVariantNumeric: 'tabular-nums' }}>
                  {inv.unsettled_sell_qty > 0 ? `−${inv.unsettled_sell_qty.toLocaleString()}` : '—'}
                </div>
                {/* Skew bar: centered, green right, red left */}
                <div style={{ position: 'relative', height: 8, backgroundColor: colors.bg.primary, borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{
                    position: 'absolute', top: 0, bottom: 0,
                    width: `${skewPct / 2}%`,
                    left: inv.skew >= 0 ? '50%' : undefined,
                    right: inv.skew < 0 ? '50%' : undefined,
                    backgroundColor: skewColor,
                    borderRadius: 4,
                  }} />
                  <div style={{ position: 'absolute', left: '50%', top: 0, bottom: 0, width: 1, backgroundColor: colors.border.default }} />
                </div>
                {/* Status */}
                <div style={{ display: 'flex', gap: 6, justifyContent: 'center', fontSize: 9, fontWeight: 700 }}>
                  <span style={{ color: inv.can_buy ? colors.accent.green : colors.border.default }}>{inv.can_buy ? '● BUY' : '✕ BUY'}</span>
                  <span style={{ color: inv.can_sell ? colors.accent.green : colors.border.default }}>{inv.can_sell ? '● SELL' : '✕ SELL'}</span>
                </div>
              </div>
            );
          })}
        </Panel>
      )}

      {/* ── Zone 6: Performance Snapshot ── */}
      {performanceSnapshot && (
        <Panel title="Performance snapshot">
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <PerfChip label="Execution Rate" value={`${(performanceSnapshot.execution_rate * 100).toFixed(1)}%`} />
            <PerfChip
              label="Sharpe Ratio"
              value={performanceSnapshot.sharpe_ratio !== null ? performanceSnapshot.sharpe_ratio.toFixed(2) : '—'}
              color={performanceSnapshot.sharpe_ratio !== null && performanceSnapshot.sharpe_ratio >= 1 ? colors.accent.green : colors.accent.yellow}
            />
            <PerfChip label="Capital Used" value={`${(performanceSnapshot.capital_utilization * 100).toFixed(1)}%`} />
            <PerfChip label="Max Drawdown" value={`${(performanceSnapshot.max_drawdown * 100).toFixed(1)}%`} color="#ff5c5c" />
            <PerfChip label="Avg Spread Capture" value={`${performanceSnapshot.avg_spread_capture_bps.toFixed(1)} bps`} />
            <PerfChip label="Quotes Generated" value={performanceSnapshot.quotes_generated.toLocaleString()} />
          </div>
        </Panel>
      )}

    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function CashCard({ label, value, color, sub }: { label: string; value: string; color: string; sub?: string }) {
  return (
    <div style={{ background: colors.bg.primary, borderRadius: 6, border: `1px solid ${color}25`, padding: '10px 12px' }}>
      <div style={{ fontSize: 9, fontWeight: 700, color: colors.text.muted, letterSpacing: '0.06em', marginBottom: 4 }}>{label.toUpperCase()}</div>
      <div style={{ fontSize: 17, fontWeight: 700, color, fontVariantNumeric: 'tabular-nums', marginBottom: 2 }}>{value}</div>
      {sub && <div style={{ fontSize: 9, color: colors.border.default }}>{sub}</div>}
    </div>
  );
}

function PerfChip({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div style={{ background: colors.bg.primary, borderRadius: 8, padding: '10px 14px', border: `1px solid ${colors.border.default}`, display: 'flex', flexDirection: 'column', gap: 4, minWidth: 120 }}>
      <div style={{ fontSize: 10, color: colors.text.muted, fontWeight: 700, letterSpacing: '0.06em' }}>{label.toUpperCase()}</div>
      <div style={{ fontSize: 16, fontWeight: 700, color: color ?? colors.text.primary }}>{value}</div>
    </div>
  );
}

/** Column totals. A P&L table without one makes the operator add up the rows by eye. */
function TotalRow({ rows, grossExposure }: { rows: Position[]; grossExposure: number }) {
  const u = rows.reduce((a, p) => a + p.unrealizedPnL, 0);
  const r = rows.reduce((a, p) => a + p.realizedPnL, 0);
  const t = u + r;
  const money = (v: number) => v.toLocaleString(undefined, { maximumFractionDigits: 0 });
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(84px,1fr) repeat(6, minmax(76px,1fr)) minmax(84px,1.05fr) minmax(56px,0.7fr) minmax(104px,1.3fr)', gap: 8, padding: '10px 16px', borderTop: `1px solid ${colors.border.default}`, alignItems: 'center', fontWeight: 700 }}>
      <div style={{ fontSize: 9, color: colors.text.muted, letterSpacing: '0.06em' }}>TOTAL</div>
      <div /><div /><div />
      <div style={{ textAlign: 'right', fontSize: 12, color: u >= 0 ? colors.accent.green : colors.accent.red, fontVariantNumeric: 'tabular-nums' }}>{money(u)}</div>
      <div style={{ textAlign: 'right', fontSize: 12, color: r >= 0 ? colors.accent.green : colors.accent.red, fontVariantNumeric: 'tabular-nums' }}>{money(r)}</div>
      <div style={{ textAlign: 'right', fontSize: 13, color: t >= 0 ? colors.accent.green : colors.accent.red, fontVariantNumeric: 'tabular-nums' }}>{t >= 0 ? '+' : ''}{money(t)}</div>
      <div style={{ textAlign: 'right', fontSize: 12, color: colors.text.secondary, fontVariantNumeric: 'tabular-nums' }}>{money(grossExposure)}</div>
      <div style={{ textAlign: 'right', fontSize: 11, color: colors.text.muted }}>100%</div>
      <div />
    </div>
  );
}

function HeaderRow() {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(84px,1fr) repeat(6, minmax(76px,1fr)) minmax(84px,1.05fr) minmax(56px,0.7fr) minmax(104px,1.3fr)', gap: 8, padding: '8px 16px', color: colors.text.muted, fontSize: 9, fontWeight: 700, borderBottom: `1px solid ${colors.border.default}`, letterSpacing: '0.06em' }}>
      <div>SYMBOL</div>
      <div style={{ textAlign: 'right' }}>QTY</div>
      <div style={{ textAlign: 'right' }}>AVG PRICE</div>
      <div style={{ textAlign: 'right' }}>CURRENT</div>
      <div style={{ textAlign: 'right' }}>UNREALIZED</div>
      <div style={{ textAlign: 'right' }}>REALIZED</div>
      <div style={{ textAlign: 'right' }}>TOTAL P&amp;L</div>
      <div style={{ textAlign: 'right' }}>EXPOSURE</div>
      <div style={{ textAlign: 'right' }}>SHARE</div>
      <div>LAST UPDATE</div>
    </div>
  );
}

function Row({ p, grossExposure }: { p: Position; grossExposure: number }) {
  // Exposure is the position marked at the live price where we have one, else at cost -- a
  // position with no current price is still capital at risk and must not read as zero.
  const exposure = Math.abs(p.quantity) * (p.currentPrice ?? p.avgPrice);
  const sharePct = grossExposure > 0 ? (exposure / grossExposure) * 100 : null;
  const totalPnL = p.totalPnL ?? (p.unrealizedPnL + p.realizedPnL);

  function formatTimestamp(time?: string): string {
    if (!time) return '—';
    try {
      const date = new Date(time);
      if (!isNaN(date.getTime())) return toEAT(date);
      return time;
    } catch { return time; }
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(84px,1fr) repeat(6, minmax(76px,1fr)) minmax(84px,1.05fr) minmax(56px,0.7fr) minmax(104px,1.3fr)', gap: 8, padding: '9px 16px', borderBottom: '1px solid #0a0d10', alignItems: 'center' }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: colors.text.primary }}>{p.symbol}</div>
      <div style={{ textAlign: 'right', color: p.quantity >= 0 ? colors.accent.green : colors.accent.red, fontWeight: 600, fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>
        {p.quantity.toLocaleString()}
      </div>
      <div style={{ textAlign: 'right', fontSize: 12, color: colors.text.secondary, fontVariantNumeric: 'tabular-nums' }}>{p.avgPrice.toFixed(2)}</div>
      <div style={{ textAlign: 'right', fontSize: 12, color: p.currentPrice ? colors.text.primary : colors.text.muted, fontVariantNumeric: 'tabular-nums' }}>
        {p.currentPrice ? p.currentPrice.toFixed(2) : '—'}
      </div>
      <div style={{ textAlign: 'right', fontSize: 12, color: p.unrealizedPnL >= 0 ? colors.accent.green : colors.accent.red, fontVariantNumeric: 'tabular-nums' }}>
        {p.unrealizedPnL.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
      </div>
      <div style={{ textAlign: 'right', fontSize: 12, color: p.realizedPnL >= 0 ? colors.accent.green : colors.accent.red, fontVariantNumeric: 'tabular-nums' }}>
        {p.realizedPnL.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
      </div>
      <div style={{ textAlign: 'right', fontSize: 13, fontWeight: 700, color: totalPnL >= 0 ? colors.accent.green : colors.accent.red, fontVariantNumeric: 'tabular-nums' }}>
        {totalPnL.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
      </div>
      <div style={{ textAlign: 'right', fontSize: 12, color: colors.text.secondary, fontVariantNumeric: 'tabular-nums' }}>
        {exposure.toLocaleString(undefined, { maximumFractionDigits: 0 })}
      </div>
      <div style={{ textAlign: 'right', fontSize: 11, color: colors.text.muted, fontVariantNumeric: 'tabular-nums' }}>
        {sharePct != null ? `${sharePct.toFixed(1)}%` : '—'}
      </div>
      <div style={{ fontSize: 10, color: colors.text.muted }}>{formatTimestamp(p.lastUpdate)}</div>
    </div>
  );
}
